/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Frontend Dynamic Domain & Asset URL Resolver (WP 12.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { resolvePlatformDomains, resolveMediaUrl, type PlatformDomains } from '@sofia/shared-types';

/**
 * Returns the fully resolved platform domains for the user workspace frontend.
 * Reads Vite environment variables with graceful fallbacks.
 * When running locally on localhost/127.0.0.1 without explicit domain overrides,
 * origins adapt to local development ports so the Vite proxy handles /api seamlessly.
 */
export function getWorkspaceDomains(): PlatformDomains {
  const env = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : ({} as any);

  const isLocalhost =
    typeof window !== 'undefined' &&
    Boolean(window.location?.hostname) &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.endsWith('.localhost'));

  if (isLocalhost && !env.VITE_BASE_DOMAIN) {
    const localOrigin = window.location.origin;
    return {
      baseDomain: 'localhost',
      siteOrigin: env.VITE_SITE_URL || 'http://localhost:4321',
      appOrigin: env.VITE_APP_URL || localOrigin,
      adminOrigin: env.VITE_ADMIN_URL || 'http://localhost:3002',
      apiOrigin: env.VITE_API_URL || localOrigin,
      mediaOrigin: env.VITE_MEDIA_CDN_DOMAIN || 'http://localhost:8787',
      docsOrigin: env.VITE_DOCS_URL || 'http://localhost:8787',
      assetsOrigin: env.VITE_ASSETS_CDN_DOMAIN || localOrigin,
    };
  }

  // Dynamically resolve base domain if on custom domain in browser
  let resolvedBase = env.VITE_BASE_DOMAIN;
  if (!resolvedBase && typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    const parts = host.split('.');
    if (parts.length >= 2) {
      resolvedBase = parts.slice(-2).join('.');
    }
  }

  return resolvePlatformDomains({
    BASE_DOMAIN: resolvedBase,
    SITE_URL: env.VITE_SITE_URL,
    APP_URL: env.VITE_APP_URL,
    ADMIN_URL: env.VITE_ADMIN_URL,
    API_URL: env.VITE_API_URL,
    MEDIA_CDN_DOMAIN: env.VITE_MEDIA_CDN_DOMAIN,
    DOCS_URL: env.VITE_DOCS_URL,
    ASSETS_CDN_DOMAIN: env.VITE_ASSETS_CDN_DOMAIN,
  });
}

/**
 * Resolves an R2 media key or relative path into a public CDN URL.
 * If already an absolute URL (http/https/data/blob), returns it unchanged.
 */
export function resolveWorkspaceMediaUrl(keyOrUrl: string | null | undefined): string {
  return resolveMediaUrl(keyOrUrl, getWorkspaceDomains().mediaOrigin);
}

/**
 * Resolves a static asset key or relative path into a public assets CDN URL.
 */
export function resolveWorkspaceAssetUrl(keyOrUrl: string | null | undefined): string {
  return resolveMediaUrl(keyOrUrl, getWorkspaceDomains().assetsOrigin);
}
